import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { useSubscription } from '../hooks/useSubscription';

// ─── Trial Banner ────────────────────────────────────────────────────────────
// Shown when user is in the 7-day Pro trial. Dismissible per session.

const TRIAL_DISMISSED_KEY = 'phrenos_trial_banner_dismissed';

export const TrialBanner: React.FC = () => {
  const { isTrial, trialDaysLeft } = useSubscription();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(TRIAL_DISMISSED_KEY) === '1'
  );

  if (!isTrial || dismissed) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(TRIAL_DISMISSED_KEY, '1');
    setDismissed(true);
  };

  const daysText = trialDaysLeft === 1 ? '1 day' : `${trialDaysLeft} days`;

  return (
    <div style={{
      backgroundColor: 'var(--color-primary)',
      color: '#fff',
      padding: '10px 16px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, fontSize: 13, fontWeight: 500,
    }}>
      <span>
        Pro Trial — <strong>{daysText} left</strong>. Subscribe to keep full access.
      </span>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
        <button
          onClick={() => navigate('/upgrade')}
          style={{
            padding: '5px 12px', borderRadius: 8,
            backgroundColor: '#fff', color: 'var(--color-primary)',
            border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer',
          }}
        >
          Subscribe
        </button>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          style={{
            background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
            cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
};

// ─── Coupon Applied Banner ────────────────────────────────────────────────────
// Shown once after a successful coupon redemption redirect (?coupon_applied=1).

export const CouponAppliedBanner: React.FC = () => {
  const { couponApplied } = useSubscription();
  const [dismissed, setDismissed] = useState(false);

  if (!couponApplied || dismissed) return null;

  return (
    <div style={{
      backgroundColor: 'var(--color-success-light)',
      borderBottom: '1px solid var(--color-confirmation)',
      color: 'var(--color-primary)',
      padding: '10px 16px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, fontSize: 13, fontWeight: 600,
    }}>
      <span>🎉 Pro access activated! Enjoy Phrenos Pro.</span>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        style={{
          background: 'none', border: 'none', color: 'var(--color-primary)',
          cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0, opacity: 0.6,
        }}
      >
        ×
      </button>
    </div>
  );
};

// ─── Past-Due Banner ──────────────────────────────────────────────────────────
// Shown when a Pro subscription's payment has failed (Stripe is retrying).
// Directs user to Customer Portal to update their card — NOT the upgrade flow.

export const PastDueBanner: React.FC = () => {
  const { isPastDue } = useSubscription();
  const [loading, setLoading] = useState(false);

  if (!isPastDue) return null;

  const handleUpdatePayment = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-portal`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ returnUrl: window.location.href }),
        }
      );
      const { url, error } = await res.json();
      if (error) throw new Error(error);
      window.location.href = url;
    } catch (err) {
      console.error('[PastDueBanner] portal error:', err);
      setLoading(false);
    }
  };

  return (
    <div style={{
      backgroundColor: '#FEF3C7', // amber-100
      borderBottom: '1px solid #F59E0B',
      color: '#92400E', // amber-800
      padding: '10px 16px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, fontSize: 13, fontWeight: 500,
    }}>
      <span>Payment failed — update your card to keep Pro access.</span>
      <button
        onClick={handleUpdatePayment}
        disabled={loading}
        style={{
          padding: '5px 12px', borderRadius: 8,
          backgroundColor: '#D97706', color: '#fff',
          border: 'none', fontWeight: 700, fontSize: 12,
          cursor: loading ? 'not-allowed' : 'pointer', flexShrink: 0,
        }}
      >
        {loading ? 'Loading…' : 'Update Payment →'}
      </button>
    </div>
  );
};
